import { z } from 'zod';
import { RequestFileMimeSchema } from '../requests/request-files.js';
import {
  CargoProblemVideoMimeSchema,
  CargoProblemVideoObjectKeySchema,
  issuedStorageObjectKeyForScope,
} from '../storage.js';
import { CargoProblemMaterialSchema } from './materials.js';
import { makeCargoProblemTransitionInputSchema } from './transition-input.js';
import {
  CARGO_PROBLEM_COMMENT_MAX,
  CARGO_PROBLEM_COMMENT_PREVIEW_MAX,
  CARGO_PROBLEM_FILE_MAX_BYTES,
  CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX,
  CARGO_PROBLEM_VIDEO_MAX_BYTES,
  isCargoProblemDocumentMime,
} from './vocab.js';
import { CargoProblemActorSchema } from './wire.js';

/**
 * @aiPurpose the message a reply points at, rendered above the reply bubble.
 * @aiInvariant replies are ONE level: this shape has no `replyTo` key of its
 *   own, and `.strict()` turns an attempt to nest one into a parse failure, so
 *   no recursive reply graph can ever cross the wire.
 */
export const CargoProblemMessageReplyPreviewSchema = z
  .object({
    id: z.string().uuid(),
    author: CargoProblemActorSchema,
    bodyPreview: z.string().max(CARGO_PROBLEM_COMMENT_PREVIEW_MAX),
    attachmentCount: z.number().int().nonnegative().max(CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX),
  })
  .strict();
export type CargoProblemMessageReplyPreview = z.infer<typeof CargoProblemMessageReplyPreviewSchema>;

/**
 * @aiPurpose one contextual chat message with the evidence that was posted with
 *   it. A NEW shape: `CargoProblemCommentSchema` stays frozen for clients
 *   loaded from the previous color.
 * @aiInvariant a row written before the workspace shipped is fully
 *   representable — `clientMessageId` null, `replyTo` null, `attachments` empty
 *   — so legacy conversation renders without a rewrite.
 */
export const CargoProblemMessageSchema = z
  .object({
    id: z.string().uuid(),
    author: CargoProblemActorSchema,
    body: z.string().max(CARGO_PROBLEM_COMMENT_MAX),
    clientMessageId: z.string().uuid().nullable(),
    replyTo: CargoProblemMessageReplyPreviewSchema.nullable(),
    attachments: z.array(CargoProblemMaterialSchema).max(CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX),
    createdAt: z.string().datetime(),
  })
  .strict();
export type CargoProblemMessage = z.infer<typeof CargoProblemMessageSchema>;

/**
 * @aiPurpose one already-uploaded object the message binds. The client has
 *   exchanged a presigned URL for `scope = 'cargo-problem-attachment'` and PUT
 *   the bytes; the message commits the metadata rows with the body atomically.
 * @aiSecurity the key is narrowed to the cargo-problem scope, not left at the
 *   broad issued-key grammar, which admits a key the caller legitimately owns
 *   in another flow (`lead-file`, `request-attachment`, …). Excluding those in
 *   the contract means no future handler can accept one by omitting the check;
 *   the service-side scope/ownership check that the legacy bind path already
 *   performs (`files.service.ts`), the MIME allowlist, and the byte ceiling
 *   remain the second layer.
 */
const CargoProblemOrdinaryAttachmentInputSchema = z
  .object({
    objectKey: issuedStorageObjectKeyForScope('cargo-problem-attachment'),
    fileName: z.string().trim().min(1).max(255),
    contentType: RequestFileMimeSchema,
    contentLength: z.number().int().positive().max(CARGO_PROBLEM_FILE_MAX_BYTES),
  })
  .strict();

const CargoProblemVideoAttachmentInputSchema = z
  .object({
    objectKey: CargoProblemVideoObjectKeySchema,
    fileName: z.string().trim().min(1).max(255),
    contentType: CargoProblemVideoMimeSchema,
    contentLength: z.number().int().positive().max(CARGO_PROBLEM_VIDEO_MAX_BYTES),
  })
  .strict();

export const CargoProblemMessageAttachmentInputSchema = z.union([
  CargoProblemOrdinaryAttachmentInputSchema,
  CargoProblemVideoAttachmentInputSchema,
]);
export type CargoProblemMessageAttachmentInput = z.infer<
  typeof CargoProblemMessageAttachmentInputSchema
>;

/**
 * @aiPurpose body for posting one message. One user intent is one operation:
 *   the body and its whole attachment set commit or fail together.
 * @aiInvariant `clientMessageId` is the caller-generated idempotency key. It is
 *   unique per problem in the database, so a retried POST after a dropped
 *   response re-resolves to the same message instead of duplicating it.
 * @aiInvariant a message must say something: a nonblank body OR at least one
 *   attachment. Evidence media may travel without text, but a PDF/Word/Excel
 *   document may not — a bare document leaves the recipient guessing.
 */
export const CargoProblemMessageCreateInputSchema = z
  .object({
    clientMessageId: z.string().uuid(),
    body: z.string().max(CARGO_PROBLEM_COMMENT_MAX).optional(),
    replyToId: z.string().uuid().optional(),
    attachments: z
      .array(CargoProblemMessageAttachmentInputSchema)
      .max(CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX)
      .optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    const attachments = input.attachments ?? [];
    const hasBody = (input.body ?? '').trim().length > 0;
    if (!hasBody && attachments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['body'],
        message: 'a message needs a nonblank body or at least one attachment',
      });
      return;
    }
    if (!hasBody && attachments.some((file) => isCargoProblemDocumentMime(file.contentType))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['body'],
        message: 'a document attachment requires a nonblank body',
      });
    }
  });
export type CargoProblemMessageCreateInput = z.infer<typeof CargoProblemMessageCreateInputSchema>;

export const CargoProblemMessageResponseSchema = z
  .object({ message: CargoProblemMessageSchema })
  .strict();
export type CargoProblemMessageResponse = z.infer<typeof CargoProblemMessageResponseSchema>;

/** Parent-scoped presign request; the server supplies the only allowed scope. */
export const CargoProblemAttachmentUploadInputSchema = z.union([
  z
    .object({
      contentType: RequestFileMimeSchema,
      contentLength: z.number().int().positive().max(CARGO_PROBLEM_FILE_MAX_BYTES),
    })
    .strict(),
  z
    .object({
      scope: z.literal('cargo-problem-video'),
      contentType: CargoProblemVideoMimeSchema,
      contentLength: z.number().int().positive().max(CARGO_PROBLEM_VIDEO_MAX_BYTES),
    })
    .strict(),
]);
export type CargoProblemAttachmentUploadInput = z.infer<
  typeof CargoProblemAttachmentUploadInputSchema
>;

/** New workspace transition contract. The legacy strict transition input stays frozen. */
export const CargoProblemWorkspaceTransitionInputSchema = makeCargoProblemTransitionInputSchema({
  attachments: z
    .array(CargoProblemMessageAttachmentInputSchema)
    .max(CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX)
    .optional(),
}).superRefine((input, ctx) => {
  if (
    !input.note &&
    input.attachments?.some((file) => isCargoProblemDocumentMime(file.contentType))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['note'],
      message: 'a document attachment requires a nonblank note',
    });
  }
});
export type CargoProblemWorkspaceTransitionInput = z.infer<
  typeof CargoProblemWorkspaceTransitionInputSchema
>;
